/*
 * SpendRevenueTrend.js — 6-month trend chart for the Executive Overview.
 *
 * Renders three visual layers on the same axis (SVG, no chart lib to keep
 * bundle size lean):
 *   • Revenue bars (yellow, tall)
 *   • Spend bars (subtle white/gray, next to revenue)
 *   • ROAS line overlay (green dashed) — hides null values gracefully
 *
 * The idea is to let operators eyeball ROAS drift month-over-month
 * without opening any drill-down.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChartLine } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortMonth = (mk) => {
  const [y, m] = mk.split("-").map(Number);
  return `${MONTH_LABELS[m - 1]} ${String(y).slice(2)}`;
};
const fmtCompact = (v, ccy) => {
  if (v == null || isNaN(v) || v === 0) return "—";
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 1, notation: "compact" }).format(v);
  } catch {
    return `${ccy} ${Math.round(v).toLocaleString("en-IN")}`;
  }
};

const SpendRevenueTrend = ({ clientId, currency = "INR", months = 6 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverIdx, setHoverIdx] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/clients/${clientId}/spend-trend`, { params: { months } })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, months]);

  const chart = useMemo(() => {
    if (!data?.points?.length) return null;
    const pts = data.points;
    const maxMoney = Math.max(...pts.flatMap(p => [p.revenue || 0, p.spend || 0]), 1);
    const maxRoas = Math.max(...pts.map(p => p.roas || 0), 1);
    return { pts, maxMoney, maxRoas };
  }, [data]);

  if (loading) {
    return (
      <div className="p-4 rounded-xl bg-[#0B0B0F] border border-white/10" data-testid="trend-chart-loading">
        <div className="h-3 bg-white/10 rounded w-40 mb-3 animate-pulse" />
        <div className="h-32 bg-white/5 rounded animate-pulse" />
      </div>
    );
  }

  if (!chart) return null;

  const { pts, maxMoney, maxRoas } = chart;
  const anyRevenue = pts.some(p => (p.revenue || 0) > 0);
  const anySpend = pts.some(p => (p.spend || 0) > 0);
  if (!anyRevenue && !anySpend) return null;

  // SVG geometry
  const width = 640;
  const height = 200;
  const padLeft = 44;
  const padRight = 40;
  const padTop = 20;
  const padBottom = 32;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;
  const groupW = chartW / pts.length;
  const barW = Math.min((groupW * 0.35), 26);
  const barGap = 4;

  // ROAS line points (skip nulls)
  const roasPts = pts
    .map((p, i) => (p.roas != null ? { i, roas: p.roas } : null))
    .filter(Boolean);

  const scaleY = (v) => padTop + chartH - (v / maxMoney) * chartH;
  const roasY = (r) => padTop + chartH - (r / maxRoas) * chartH;

  const hoveredPt = hoverIdx != null ? pts[hoverIdx] : null;

  return (
    <div className="p-4 rounded-xl bg-[#0B0B0F] border border-white/10 space-y-3" data-testid="spend-revenue-trend">
      <div className="flex items-baseline justify-between">
        <div>
          <h4 className="text-sm font-semibold text-white flex items-center gap-2" style={{ fontFamily: "Varela Round, sans-serif" }}>
            <ChartLine size={14} weight="fill" className="text-[#FFEB00]" /> Spend vs Revenue · last {months} months
          </h4>
          <p className="text-[10px] text-white/50 mt-0.5">Bars = Revenue (yellow) vs Spend (grey) · dashed line = ROAS</p>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-[10px] text-white/70" data-testid="trend-legend">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FFEB00]" /> Revenue</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-white/40" /> Spend</span>
          <span className="flex items-center gap-1"><span className="w-3 h-[2px] bg-emerald-400" style={{ borderTop: "1px dashed" }} /> ROAS</span>
        </div>
      </div>

      <div className="relative w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full h-auto min-w-[500px]"
          style={{ maxHeight: 220 }}
          data-testid="trend-svg"
        >
          {/* Y-axis grid lines */}
          {[0.25, 0.5, 0.75, 1].map((frac) => (
            <line
              key={frac}
              x1={padLeft}
              x2={width - padRight}
              y1={padTop + chartH * (1 - frac)}
              y2={padTop + chartH * (1 - frac)}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="1"
            />
          ))}

          {/* Bars */}
          {pts.map((p, i) => {
            const cx = padLeft + i * groupW + groupW / 2;
            const revX = cx - barW - barGap / 2;
            const spendX = cx + barGap / 2;
            const revH = ((p.revenue || 0) / maxMoney) * chartH;
            const spendH = ((p.spend || 0) / maxMoney) * chartH;
            return (
              <g
                key={p.month}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                style={{ cursor: "pointer" }}
                data-testid={`trend-month-${p.month}`}
              >
                {/* Hover strip */}
                <rect
                  x={padLeft + i * groupW}
                  y={padTop}
                  width={groupW}
                  height={chartH}
                  fill={hoverIdx === i ? "rgba(255,235,0,0.05)" : "transparent"}
                />
                {p.revenue > 0 && (
                  <rect
                    x={revX}
                    y={padTop + chartH - revH}
                    width={barW}
                    height={revH}
                    fill="#FFEB00"
                    opacity={hoverIdx == null || hoverIdx === i ? 0.9 : 0.4}
                    rx="1.5"
                  />
                )}
                {p.spend > 0 && (
                  <rect
                    x={spendX}
                    y={padTop + chartH - spendH}
                    width={barW}
                    height={spendH}
                    fill="rgba(255,255,255,0.5)"
                    opacity={hoverIdx == null || hoverIdx === i ? 0.9 : 0.4}
                    rx="1.5"
                  />
                )}
                {/* X-axis label */}
                <text
                  x={cx}
                  y={height - padBottom + 16}
                  fontSize="10"
                  fill={hoverIdx === i ? "#FFEB00" : "rgba(255,255,255,0.6)"}
                  textAnchor="middle"
                  fontFamily="sans-serif"
                >
                  {shortMonth(p.month)}
                </text>
              </g>
            );
          })}

          {/* ROAS line */}
          {roasPts.length >= 2 && (
            <>
              <polyline
                points={roasPts.map(({ i, roas }) => {
                  const cx = padLeft + i * groupW + groupW / 2;
                  return `${cx},${roasY(roas)}`;
                }).join(" ")}
                fill="none"
                stroke="rgba(74,222,128,0.9)"
                strokeWidth="1.75"
                strokeDasharray="4 3"
                strokeLinejoin="round"
                data-testid="trend-roas-line"
              />
              {roasPts.map(({ i, roas }) => {
                const cx = padLeft + i * groupW + groupW / 2;
                return (
                  <circle
                    key={i}
                    cx={cx}
                    cy={roasY(roas)}
                    r={hoverIdx === i ? 4 : 2.5}
                    fill="rgba(74,222,128,1)"
                    stroke="#0B0B0F"
                    strokeWidth="1.5"
                  />
                );
              })}
            </>
          )}

          {/* Left Y-axis labels (money) */}
          {[0, 0.5, 1].map((frac) => (
            <text
              key={frac}
              x={padLeft - 6}
              y={padTop + chartH * (1 - frac) + 3}
              fontSize="9"
              fill="rgba(255,255,255,0.45)"
              textAnchor="end"
              fontFamily="sans-serif"
            >
              {fmtCompact(maxMoney * frac, currency)}
            </text>
          ))}

          {/* Right Y-axis labels (ROAS) */}
          {maxRoas > 1 && [0, 0.5, 1].map((frac) => (
            <text
              key={`r-${frac}`}
              x={width - padRight + 6}
              y={padTop + chartH * (1 - frac) + 3}
              fontSize="9"
              fill="rgba(74,222,128,0.7)"
              textAnchor="start"
              fontFamily="sans-serif"
            >
              {(maxRoas * frac).toFixed(1)}×
            </text>
          ))}
        </svg>

        {/* Hover tooltip */}
        {hoveredPt && (
          <div
            className="absolute top-2 right-2 p-2.5 rounded-lg bg-black/90 border border-white/20 text-[11px] text-white shadow-2xl min-w-[140px]"
            data-testid="trend-tooltip"
          >
            <p className="font-semibold text-[#FFEB00] mb-1">{shortMonth(hoveredPt.month)}</p>
            <div className="space-y-0.5">
              <p className="flex justify-between gap-3"><span className="text-white/60">Revenue</span> <span className="font-mono">{fmtCompact(hoveredPt.revenue, currency)}</span></p>
              <p className="flex justify-between gap-3"><span className="text-white/60">Spend</span> <span className="font-mono">{fmtCompact(hoveredPt.spend, currency)}</span></p>
              <p className="flex justify-between gap-3"><span className="text-white/60">ROAS</span> <span className="font-mono text-emerald-400">{hoveredPt.roas != null ? `${hoveredPt.roas.toFixed(2)}×` : "—"}</span></p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpendRevenueTrend;
